import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, CheckCircle, XCircle, Clock,
  Lightbulb, RotateCcw, Trophy, ArrowRight
} from 'lucide-react';
import { client } from '../api/client';
import { useAuthStore } from '../store/authStore';

interface Question {
  id: number;
  topicId: number;
  type: string;
  difficulty: number;
  question_text: string;
  question_image?: string;
  options?: string;
  correct_answer: string;
  explanation?: string;
}

interface Topic {
  id: number;
  name: string;
  nameCn: string;
  grade: number;
}

export function PracticePage() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [topic, setTopic] = useState<Topic | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState<number>(0);

  const currentQuestion = questions[currentIndex];

  useEffect(() => {
    if (topicId) {
      loadQuestions();
    }
  }, [topicId]);

  useEffect(() => {
    setStartTime(Date.now());
  }, [currentIndex]);

  async function loadQuestions() {
    setLoading(true);
    try {
      // Load topic info
      const topicsRes = await client.api.fetch(`/api/public/topics`);
      const topicsData = await topicsRes.json();
      const foundTopic = (topicsData.data || []).find(
        (t: Topic) => t.id === parseInt(topicId || '0')
      );
      setTopic(foundTopic || null);

      // Load questions
      const questionsRes = await client.api.fetch(
        `/api/public/questions?topic_id=${topicId}&limit=10`
      );
      const questionsData = await questionsRes.json();
      setQuestions(questionsData.data || []);
    } catch (error) {
      console.error('Failed to load questions:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitAnswer() {
    if (!currentQuestion || selectedAnswer === null) return;

    const isCorrect = selectedAnswer === currentQuestion.correct_answer;
    setShowResult(true);
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1,
    }));

    // Record practice if logged in
    if (user) {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      try {
        await client.api.fetch('/api/practice', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            questionId: currentQuestion.id,
            userAnswer: selectedAnswer,
            isCorrect,
            timeSpent,
          }),
        });
      } catch (error) {
        console.error('Failed to record practice:', error);
      }
    }
  }

  function handleNextQuestion() {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setShowExplanation(false);
    }
  }

  function handleRestart() {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setShowExplanation(false);
    setScore({ correct: 0, total: 0 });
  }

  function parseOptions(optionsStr?: string): string[] {
    if (!optionsStr) return [];
    try {
      return JSON.parse(optionsStr);
    } catch {
      return [];
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lightbulb className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-700 mb-2">暂无题目</h2>
          <p className="text-slate-500 mb-6">该知识点的题目正在准备中</p>
          <Link
            to="/subjects"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            返回题库
          </Link>
        </div>
      </div>
    );
  }

  // Practice Complete
  if (currentIndex >= questions.length || (showResult && currentIndex === questions.length - 1 && selectedAnswer !== null)) {
    const percentage = Math.round((score.correct / score.total) * 100);
    const isLastQuestion = currentIndex === questions.length - 1 && showResult;

    if (isLastQuestion) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">练习完成！</h2>
            <p className="text-slate-600 mb-6">{topic?.nameCn}</p>
            
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <div className="text-5xl font-bold text-slate-900 mb-2">
                {percentage}%
              </div>
              <div className="text-slate-500">
                正确 {score.correct} / {score.total} 题
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleRestart}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                再练一次
              </button>
              <Link
                to="/subjects"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                返回题库
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      );
    }
  }

  const options = parseOptions(currentQuestion?.options);
  const isCorrect = selectedAnswer === currentQuestion?.correct_answer;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/subjects"
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            返回
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">
              {currentIndex + 1} / {questions.length}
            </span>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <CheckCircle className="w-4 h-4" />
              {score.correct}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-slate-200 rounded-full mb-8 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600"
            initial={{ width: 0 }}
            animate={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>

        {/* Question Card */}
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6"
        >
          {/* Difficulty Badge */}
          <div className="flex items-center gap-2 mb-4">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              currentQuestion?.difficulty <= 2 ? 'bg-green-100 text-green-700' :
              currentQuestion?.difficulty <= 3 ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              难度 {currentQuestion?.difficulty}/5
            </span>
            <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600">
              {currentQuestion?.type === 'multiple_choice' ? '选择题' :
               currentQuestion?.type === 'fill_blank' ? '填空题' : '问答题'}
            </span>
          </div>

          {/* Question Text */}
          <h2 className="text-lg font-medium text-slate-900 mb-6 leading-relaxed">
            {currentQuestion?.question_text}
          </h2>

          {/* Options */}
          {currentQuestion?.type === 'multiple_choice' && (
            <div className="space-y-3">
              {options.map((option, index) => {
                const optionLetter = String.fromCharCode(65 + index);
                const isSelected = selectedAnswer === optionLetter;
                const isCorrectOption = optionLetter === currentQuestion.correct_answer;
                
                let optionClass = 'border-slate-200 hover:border-blue-300 hover:bg-blue-50';
                if (showResult) {
                  if (isCorrectOption) {
                    optionClass = 'border-green-500 bg-green-50';
                  } else if (isSelected && !isCorrectOption) {
                    optionClass = 'border-red-500 bg-red-50';
                  }
                } else if (isSelected) {
                  optionClass = 'border-blue-500 bg-blue-50';
                }

                return (
                  <button
                    key={index}
                    onClick={() => !showResult && setSelectedAnswer(optionLetter)}
                    disabled={showResult}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${optionClass}`}
                  >
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold ${
                      showResult && isCorrectOption ? 'bg-green-500 text-white' :
                      showResult && isSelected && !isCorrectOption ? 'bg-red-500 text-white' :
                      isSelected ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {optionLetter}
                    </span>
                    <span className="flex-1 text-slate-700">{option}</span>
                    {showResult && isCorrectOption && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    {showResult && isSelected && !isCorrectOption && (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Fill in the blank */}
          {currentQuestion?.type === 'fill_blank' && (
            <input
              type="text"
              value={selectedAnswer || ''}
              onChange={(e) => !showResult && setSelectedAnswer(e.target.value)}
              disabled={showResult}
              placeholder="输入你的答案..."
              className={`w-full px-4 py-3 rounded-xl border-2 text-lg transition-all ${
                showResult
                  ? isCorrect
                    ? 'border-green-500 bg-green-50'
                    : 'border-red-500 bg-red-50'
                  : 'border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
              }`}
            />
          )}
        </motion.div>

        {/* Result & Explanation */}
        <AnimatePresence>
          {showResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`rounded-xl p-4 mb-6 ${
                isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {isCorrect ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-700">回答正确！</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="font-semibold text-red-700">
                      回答错误，正确答案是 {currentQuestion?.correct_answer}
                    </span>
                  </>
                )}
              </div>
              
              {currentQuestion?.explanation && (
                <button
                  onClick={() => setShowExplanation(!showExplanation)}
                  className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
                >
                  <Lightbulb className="w-4 h-4" />
                  {showExplanation ? '隐藏解析' : '查看解析'}
                </button>
              )}
              
              {showExplanation && currentQuestion?.explanation && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 text-sm text-slate-700 leading-relaxed"
                >
                  {currentQuestion.explanation}
                </motion.p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex gap-3">
          {!showResult ? (
            <button
              onClick={handleSubmitAnswer}
              disabled={selectedAnswer === null}
              className="flex-1 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              提交答案
            </button>
          ) : (
            <button
              onClick={handleNextQuestion}
              className="flex-1 flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
            >
              {currentIndex < questions.length - 1 ? '下一题' : '查看结果'}
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
